import { parseThemeColors } from '../gameTemplates';

export const flashcardsTemplate = (data) => {
  const { title, questions, themeColors, themeFonts, backgroundImage } = data;
  const colors = parseThemeColors(themeColors);
  
  return `const InteractiveElement = () => {
  const [currentCard, setCurrentCard] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);
  const traceIdRef = useRef(crypto.randomUUID());
  const startTimeRef = useRef(Date.now());

  const questions = ${JSON.stringify(questions, null, 2)};

  useEffect(() => {
    trackEvent('interactive_start', traceIdRef.current, {
      startTime: new Date().toISOString(),
      status: 'started'
    });
  }, []);

  useEffect(() => {
    if (gameCompleted) {
      const endTime = new Date().toISOString();
      const timeTaken = Math.floor((Date.now() - startTimeRef.current) / 1000);
      
      trackEvent('interactive_end', traceIdRef.current, {
        endTime,
        timeTaken,
        status: 'completed'
      });
    }
  }, [gameCompleted]);

  const flipAnimation = useSpring({
    transform: \`perspective(2000px) rotateY(\${flipped ? 180 : 0}deg)\`,
    config: { mass: 2, tension: 180, friction: 50, clamp: true },
  });

  const handleFlip = () => {
    setFlipped(!flipped);
  };

  const handleNext = () => {
    if (currentCard === questions.length - 1) {
      setGameCompleted(true);
    } else {
      setCurrentCard(currentCard + 1);
      setFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentCard > 0) {
      setCurrentCard(currentCard - 1);
      setFlipped(false);
    }
  };

  const resetGame = () => {
    setGameCompleted(false);
    setCurrentCard(0);
    setFlipped(false);
    traceIdRef.current = crypto.randomUUID();
    startTimeRef.current = Date.now();
    trackEvent('interactive_start', traceIdRef.current, {
      startTime: new Date().toISOString(),
      status: 'started'
    });
  };

  const ConfettiPiece = ({ x, y, color, shape, delay }) => {
    const spring = useSpring({
      from: { y: -20, opacity: 1, rotate: 0, scale: 0 },
      to: async (next) => {
        await next({ y: y + Math.random() * 100, opacity: 1, rotate: 360, scale: 1, config: { duration: 1000 } });
        await next({ y: y + 200, opacity: 0, config: { duration: 1000 } });
      },
      config: { mass: 1, tension: 120, friction: 30 },
      delay: delay * 1000,
    });

    return (
      <animated.div
        style={{
          position: 'absolute',
          left: \`\${x}%\`,
          top: \`\${y}%\`,
          transform: spring.rotate.to(r => \`rotate(\${r}deg) scale(\${spring.scale.get()})\`),
          y: spring.y,
          opacity: spring.opacity,
        }}
      >
        <span style={{ color, fontSize: '2rem', filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.3))' }}>
          {shape}
        </span>
      </animated.div>
    );
  };

  const Confetti = () => {
    const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD'];
    const shapes = ['★', '●', '♦', '♠', '♥', '♣', '✦', '✧'];
    
    const confettiItems = useMemo(() => Array.from({ length: 100 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 50,
      color: colors[Math.floor(Math.random() * colors.length)],
      shape: shapes[Math.floor(Math.random() * shapes.length)],
      delay: Math.random() * 3
    })), []);

    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
        {confettiItems.map((item) => (
          <ConfettiPiece key={item.id} {...item} />
        ))}
      </div>
    );
  };

  return (
    <div 
      className="min-h-screen w-full flex items-center justify-center p-4"
      style={{
        backgroundImage: 'url(${backgroundImage})',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        fontFamily: '${themeFonts}'
      }}
    >
      <Card className="w-[85%] md:w-[80%] lg:w-[75%] mx-auto bg-white/90 shadow-[6px_6px_12px_#bebebe,-6px_-6px_12px_#ffffff] relative overflow-hidden">
        {gameCompleted && <div className="absolute inset-0"><Confetti /></div>}

        <CardContent className="p-2 lg:p-4 mt-8 mx-8">
          {!gameCompleted ? (
            <div className="w-full h-auto min-h-[90%] flex flex-col items-center justify-center space-y-6">
              <div className="relative w-full max-w-2xl mx-auto">
                <div className="absolute top-12 left-1/2 -translate-x-1/2 w-[95%] aspect-[16/9] rounded-xl bg-[${colors.primary}]/30" />
                <div className="absolute top-6 left-1/2 -translate-x-1/2 w-[97%] aspect-[16/9] rounded-xl bg-[${colors.primary}]/50" />
                
                <animated.div
                  style={{
                    ...flipAnimation,
                    transformStyle: 'preserve-3d',
                  }}
                  className="relative w-full aspect-[16/9] cursor-pointer"
                  onClick={handleFlip}
                >
                  <div className={\`absolute w-full h-full rounded-xl px-8 py-4 flex items-center justify-center backface-hidden \${flipped ? 'hidden' : ''}\`} style={{ backgroundColor: '${colors.primary}' }}>
                    <p className="text-lg lg:text-2xl font-medium text-white text-center">
                      <span dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(questions[currentCard].question) 
                      }}></span>
                    </p>
                  </div>
                  <div 
                    className={\`absolute w-full h-full rounded-xl px-8 py-4 flex items-center justify-center backface-hidden \${flipped ? '' : 'hidden'}\`}
                    style={{ transform: 'rotateY(180deg)', backgroundColor: '#E9E8F4' }}
                  >
                    <p className="text-lg lg:text-2xl font-medium text-[${colors.primary}] text-center">
                      <span dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(questions[currentCard].answer) 
                      }}></span>
                    </p>
                  </div>
                </animated.div>
              </div>
              
              <p className="text-xs lg:text-sm text-gray-500 pt-4 italic">Tap card to flip</p>
              <div className="flex gap-4">
                <Button
                  onClick={handlePrevious}
                  disabled={currentCard === 0}
                  className="text-xs lg:text-base px-4 py-2 lg:px-8 lg:py-4 h-auto text-wrap shadow-[6px_6px_12px_#bebebe,-6px_-6px_12px_#ffffff] bg-white hover:bg-gray-50 active:shadow-[inset_4px_4px_12px_#bebebe,inset_-4px_-4px_12px_#ffffff] disabled:opacity-50"
                  style={{ color: '${colors.primary}' }}
                >
                  Previous
                </Button>
                <Button
                  onClick={handleNext}
                  className="text-xs lg:text-base px-4 py-2 lg:px-8 lg:py-4 h-auto text-wrap shadow-[6px_6px_12px_#bebebe,-6px_-6px_12px_#ffffff] hover:opacity-90 active:shadow-[inset_4px_4px_12px_rgba(0,0,0,0.2),inset_-4px_-4px_12px_rgba(255,255,255,0.1)]"
                  style={{ backgroundColor: '${colors.primary}', color: 'white' }}
                >
                  {currentCard === questions.length - 1 ? 'Finish' : 'Next'}
                </Button>
              </div>
              <p className="text-sm text-gray-600">
                Card {currentCard + 1} of {questions.length}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center space-y-8 py-12 relative">
              <div className="text-6xl animate-bounce">🎉</div>
              <h2 className="text-3xl lg:text-4xl font-bold text-center" style={{ color: '${colors.primary}' }}>
                Congratulations!
              </h2>
              <p className="text-lg lg:text-xl text-gray-600 text-center">
                You've completed all the flashcards!
              </p>
              <Button
                onClick={resetGame}
                className="text-xs lg:text-base px-4 py-2 lg:px-8 lg:py-4 h-auto text-wrap shadow-[6px_6px_12px_#bebebe,-6px_-6px_12px_#ffffff] hover:opacity-90 active:shadow-[inset_4px_4px_12px_rgba(0,0,0,0.2),inset_-4px_-4px_12px_rgba(255,255,255,0.1)]"
                style={{ backgroundColor: '${colors.primary}', color: 'white' }}
              >
                Start Over
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}`;
};